// filepath: apps/web/src/features/admin/effects/EmailChannelFormDialog.tsx
//
// FR-UI-ADM-8 -- create form dialog for email channels.
//
// FR-I18N-1: the body field has three locale tabs (ru/kk/en).
// At least one locale must be non-empty before the form can submit.
//
// INV-5 contract: on create success, the response carries
// `smtp_secret_value` EXACTLY ONCE. The parent owns the
// `successSmtpSecretValue` state and clears it on dialog close
// (post-ISS-206 controlled-prop pattern).

import React, { useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type { CreateEmailChannelRequest, LocaleBody } from "./types";

interface EmailChannelFormDialogProps {
  open: boolean;
  onSubmit: (req: CreateEmailChannelRequest) => Promise<void>;
  onClose: () => void;
  isPending: boolean;
  successSmtpSecretValue?: string | null;
  error?: string | null;
}

type LocaleTab = "ru" | "kk" | "en";

export function EmailChannelFormDialog({
  open,
  onSubmit,
  onClose,
  isPending,
  successSmtpSecretValue,
  error,
}: EmailChannelFormDialogProps): React.ReactElement | null {
  const { t } = useTranslation();
  const [fromAddress, setFromAddress] = useState("");
  const [subject, setSubject] = useState("");
  const [provider, setProvider] = useState("smtp");
  const [activeTab, setActiveTab] = useState<LocaleTab>("ru");
  const [bodyRu, setBodyRu] = useState("");
  const [bodyKk, setBodyKk] = useState("");
  const [bodyEn, setBodyEn] = useState("");
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  // One-time success screen (INV-5).
  if (successSmtpSecretValue !== null && successSmtpSecretValue !== undefined) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="email-channel-create-success-title"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        data-testid="email-channel-create-success-dialog"
      >
        <div className="w-full max-w-md rounded-md border border-border bg-background p-4 shadow-lg">
          <h2
            id="email-channel-create-success-title"
            className="text-sm font-semibold text-green-600"
          >
            {t("admin.effects.email.dialog.create.success.title")}
          </h2>
          <p className="mt-2 text-xs text-muted-foreground">
            {t("admin.effects.email.dialog.create.success.description")}
          </p>
          <div className="mt-3 flex gap-2 items-center">
            <code
              className="flex-1 rounded bg-muted px-2 py-1 text-xs font-mono break-all"
              data-testid="email-channel-smtp-secret-value"
            >
              {successSmtpSecretValue}
            </code>
            <button
              type="button"
              className="shrink-0 rounded border border-border px-2 py-1 text-xs"
              onClick={() => {
                void navigator.clipboard.writeText(successSmtpSecretValue);
                setCopied(true);
              }}
              data-testid="email-channel-copy-secret"
            >
              {copied
                ? t("common.copied")
                : t("admin.effects.email.dialog.create.success.copy_secret")}
            </button>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              className="rounded bg-primary px-4 py-2 text-xs text-primary-foreground"
              onClick={() => {
                setCopied(false);
                onClose();
              }}
              data-testid="email-channel-success-done"
            >
              {t("common.done")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const body: LocaleBody = {};
    if (bodyRu.trim()) body.ru = bodyRu.trim();
    if (bodyKk.trim()) body.kk = bodyKk.trim();
    if (bodyEn.trim()) body.en = bodyEn.trim();

    const req: CreateEmailChannelRequest = {
      provider,
      from_address: fromAddress.trim(),
      subject: subject.trim(),
      body,
    };
    await onSubmit(req);
  };

  const tabs: LocaleTab[] = ["ru", "kk", "en"];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="email-channel-form-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="email-channel-form-dialog"
    >
      <div className="w-full max-w-lg rounded-md border border-border bg-background p-4 shadow-lg">
        <h2 id="email-channel-form-title" className="text-sm font-semibold">
          {t("admin.effects.email.dialog.create.title")}
        </h2>
        {error && (
          <p
            role="alert"
            className="mt-2 text-xs text-destructive"
            data-testid="email-channel-form-error"
          >
            {error}
          </p>
        )}
        <form onSubmit={(e) => void handleSubmit(e)} className="mt-3 space-y-3">
          <div>
            <label className="text-xs font-medium" htmlFor="email-ch-from">
              {t("admin.effects.email.field.from_address")}
            </label>
            <input
              id="email-ch-from"
              type="email"
              value={fromAddress}
              onChange={(e) => setFromAddress(e.target.value)}
              className="mt-1 w-full rounded border border-input px-2 py-1 text-xs"
              required
              aria-required="true"
              data-testid="email-channel-from-input"
            />
          </div>
          <div>
            <label className="text-xs font-medium" htmlFor="email-ch-subject">
              {t("admin.effects.email.field.subject")}
            </label>
            <input
              id="email-ch-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1 w-full rounded border border-input px-2 py-1 text-xs"
              required
              aria-required="true"
              data-testid="email-channel-subject-input"
            />
          </div>
          <div>
            <label className="text-xs font-medium" htmlFor="email-ch-provider">
              {t("admin.effects.email.field.provider")}
            </label>
            <select
              id="email-ch-provider"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              className="mt-1 w-full rounded border border-input px-2 py-1 text-xs"
              data-testid="email-channel-provider-select"
            >
              {["smtp", "ses", "sendgrid", "mailgun", "postmark"].map((p) => (
                <option key={p} value={p}>
                  {p.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
          {/* FR-I18N-1 locale-aware body with tabs */}
          <div>
            <p className="text-xs font-medium">
              {t("admin.effects.email.field.body")}
            </p>
            <div
              role="tablist"
              className="mt-1 flex gap-1 border-b border-border"
            >
              {tabs.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab}
                  aria-controls={`email-body-${tab}`}
                  className={`px-3 py-1 text-xs uppercase ${
                    activeTab === tab
                      ? "border-b-2 border-primary font-medium"
                      : "text-muted-foreground"
                  }`}
                  onClick={() => setActiveTab(tab)}
                  data-testid={`email-body-tab-${tab}`}
                >
                  {tab}
                </button>
              ))}
            </div>
            {tabs.map((tab) => (
              <div
                key={tab}
                id={`email-body-${tab}`}
                role="tabpanel"
                aria-labelledby={tab}
                hidden={activeTab !== tab}
                data-testid={`email-body-panel-${tab}`}
              >
                <textarea
                  className="mt-1 h-20 w-full rounded border border-input px-2 py-1 text-xs"
                  value={tab === "ru" ? bodyRu : tab === "kk" ? bodyKk : bodyEn}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (tab === "ru") setBodyRu(v);
                    else if (tab === "kk") setBodyKk(v);
                    else setBodyEn(v);
                  }}
                  aria-label={`${t("admin.effects.email.field.body")} (${tab.toUpperCase()})`}
                  data-testid={`email-body-textarea-${tab}`}
                />
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="rounded border border-border px-3 py-1 text-xs"
              onClick={onClose}
              data-testid="email-channel-form-cancel"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              className="rounded bg-primary px-3 py-1 text-xs text-primary-foreground"
              disabled={isPending}
              data-testid="email-channel-form-submit"
            >
              {isPending ? t("common.saving") : t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
